import React, { useEffect, useMemo, useRef, useState } from "react";
import Fuse from "fuse.js";
import { HiOutlineSearch, HiX } from "react-icons/hi";
import { BiChevronRight } from "react-icons/bi";

const MenuSearch = ({ menuItems = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const containerRef = useRef(null);

  const fuse = useMemo(() => {
    if (!menuItems.length) return null;
    return new Fuse(menuItems, {
      threshold: 0.35,
      ignoreLocation: true,
      keys: [
        { name: "Item Name", weight: 0.6 },
        { name: "Item Description", weight: 0.2 },
        { name: "Level 1", weight: 0.1 },
        { name: "Level 2", weight: 0.1 },
      ],
    });
  }, [menuItems]);

  useEffect(() => {
    if (!fuse) return;
    if (searchTerm.trim().length >= 2) {
      const searchResults = fuse.search(searchTerm);
      const top3 = searchResults.slice(0, 3).map((r) => r.item);
      setResults(top3);
    } else {
      setResults([]);
    }
  }, [searchTerm, fuse]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
        setResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    setIsOpen(false);
    setSearchTerm("");
    setResults([]);
    const encodedName = encodeURIComponent(item["Item Name"]);
    const element = document.querySelector(`[data-item-name="${encodedName}"]`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.add("ring-2", "ring-black");
      setTimeout(() => element.classList.remove("ring-2", "ring-black"), 1500);
    }
  };

  return (
    /* CRITICAL FIX: 'fixed inset-0' combined with 'pointer-events-none'.
      This container covers the screen but allows all clicks to pass through to the dishes.
    */
    <div className="fixed top-6 left-6 z-[49]">

      {/* Container for Button + Card: 'pointer-events-auto' makes them clickable again */}
      <div ref={containerRef} className="relative pointer-events-auto">

        {/* --- Toggle Button --- */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            group relative w-14 h-14 flex items-center justify-center rounded-2xl
            transition-opacity transition-transform duration-200 shadow-2xl active:scale-90
            ${isOpen
              ? "bg-zinc-900 text-white rotate-90"
              : "bg-white/90 text-zinc-900 border border-white/20"
            }
          `}
        >
          <div className="relative w-6 h-6 flex items-center justify-center">
            {isOpen ? <HiX size={24} /> : <HiOutlineSearch size={24} />}
          </div>
          {!isOpen && (
            <span className="absolute inset-0 rounded-2xl bg-zinc-400/10 -z-10" />
          )}
        </button>

        {/* --- Search Card (Opens Downward) --- */}
        <div
          className={`
            absolute top-full left-0 mt-4 w-72 sm:w-80 rounded-[2rem] overflow-hidden 
            shadow-xl transition-opacity transition-transform duration-200 
            ease-[cubic-bezier(0.16,1,0.3,1)] origin-top-left
            ${isOpen
              ? "opacity-100 scale-100 translate-y-0"
              : "opacity-0 scale-75 -translate-y-10 pointer-events-none"
            }
          `}
        >
          <div className="bg-zinc-900/95 border border-white/10 p-3">
            <div className="relative">
              <HiOutlineSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
              <input
                autoFocus={isOpen}
                type="text"
                placeholder="Search ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white rounded-[1.5rem] pl-11 pr-4 py-3.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-opacity transition-transform placeholder:text-zinc-600"
              />
            </div>

            {results.length > 0 && (
              <div className="mt-3 max-h-60 overflow-y-auto no-scrollbar space-y-1">
                {results.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelect(item)}
                    className="group flex items-center justify-between p-3 rounded-2xl cursor-pointer hover:bg-white/10 transition-opacity transition-transform duration-200"
                  >
                    <span className="text-sm font-semibold text-zinc-300 group-hover:text-white group-hover:translate-x-1 transition-transform line-clamp-1 pr-4">
                      {item["Item Name"]}
                    </span>
                    <BiChevronRight className="text-zinc-600 group-hover:text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity transition-transform" size={18} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* BACKDROP OVERLAY: 
        Only active ('pointer-events-auto') when 'isOpen' is true. 
      */} 

      <style jsx>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default MenuSearch;